/**
 * Animation frame processor core.
 *
 * Parses animation protocol frames (VP8-wrapped metadata) and emits events.
 * Used by the animation worker that the provider installs on video receivers
 * via RTCRtpScriptTransform.
 *
 * Frame format after depacketization:
 * [0-9]     VP8 frame header (10 bytes) - minimal keyframe structure, skipped
 * [10]      flags (uint8) - packet flags
 * [11-14]   msgLen (little-endian u32) - length of protobuf message
 * [15..15+msgLen)  protobuf Message binary
 *
 * @internal
 * @packageDocumentation
 */

import type { AnimationTransformEvent } from './types';

// VP8 header size (frame tag + sync code + dimensions, descriptor already stripped)
const VP8_FRAME_HEADER_SIZE = 10;

// Protocol constants (must match egress-server/metadata_track.go)
const METADATA_FIXED_HEADER_SIZE = 5; // 1B flags + 4B length

// Packet flags
const PACKET_FLAG_IDLE = 0x01;
const PACKET_FLAG_START = 0x02;
const PACKET_FLAG_END = 0x04;
const PACKET_FLAG_GZIPPED = 0x08;
const PACKET_FLAG_TRANSITION = 0x10;
const PACKET_FLAG_TRANSITION_END = 0x20;
const PACKET_FLAG_REDUNDANT = 0x40;

/** All recognized flag bits - used by the strict sniffer. */
const KNOWN_FLAGS_MASK =
  PACKET_FLAG_IDLE |
  PACKET_FLAG_START |
  PACKET_FLAG_END |
  PACKET_FLAG_GZIPPED |
  PACKET_FLAG_TRANSITION |
  PACKET_FLAG_TRANSITION_END |
  PACKET_FLAG_REDUNDANT;

/** Sanity bound for msgLen when sniffing frames. */
const MAX_SANE_MESSAGE_LENGTH = 4 * 1024 * 1024;

/**
 * Strict check whether a frame looks like an animation protocol frame.
 * Requires an exact length match so real video frames never match.
 * @internal
 */
export function sniffAnimationFrame(data: Uint8Array): boolean {
  if (data.byteLength < VP8_FRAME_HEADER_SIZE + METADATA_FIXED_HEADER_SIZE) {
    return false;
  }
  const flags = data[VP8_FRAME_HEADER_SIZE];
  if ((flags & ~KNOWN_FLAGS_MASK) !== 0) return false;

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const msgLen = view.getUint32(VP8_FRAME_HEADER_SIZE + 1, true);
  if (msgLen > MAX_SANE_MESSAGE_LENGTH) return false;

  return (
    data.byteLength ===
    VP8_FRAME_HEADER_SIZE + METADATA_FIXED_HEADER_SIZE + msgLen
  );
}

interface FrameMetadata {
  flags: number;
  protobufLength: number;
  protobufData: Uint8Array;
  hasRedundant: boolean;
}

function parseMetadataHeader(data: Uint8Array): FrameMetadata | null {
  if (data.byteLength < METADATA_FIXED_HEADER_SIZE) return null;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const flags = data[0];
  const msgLen = view.getUint32(1, true); // little-endian

  const headerSize = METADATA_FIXED_HEADER_SIZE + msgLen;
  if (data.byteLength < headerSize) return null;

  const compressedData = data.slice(
    METADATA_FIXED_HEADER_SIZE,
    METADATA_FIXED_HEADER_SIZE + msgLen,
  );

  return {
    flags,
    protobufLength: msgLen,
    protobufData: compressedData, // still compressed at this point
    hasRedundant: (flags & PACKET_FLAG_REDUNDANT) !== 0,
  };
}

function isIdlePacket(flags: number): boolean {
  return (flags & PACKET_FLAG_IDLE) !== 0;
}

function isStartPacket(flags: number): boolean {
  return (flags & PACKET_FLAG_START) !== 0;
}

function isEndPacket(flags: number): boolean {
  return (flags & PACKET_FLAG_END) !== 0;
}

function isGzipped(flags: number): boolean {
  return (flags & PACKET_FLAG_GZIPPED) !== 0;
}

function isTransitionPacket(flags: number): boolean {
  return (flags & PACKET_FLAG_TRANSITION) !== 0;
}

function isTransitionEndPacket(flags: number): boolean {
  return (flags & PACKET_FLAG_TRANSITION_END) !== 0;
}

async function decompressGzip(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  // Create a copy to ensure we have a plain ArrayBuffer, not SharedArrayBuffer
  const copy = new Uint8Array(data);
  writer.write(copy);
  writer.close();

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    totalLength += value.length;
  }

  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result;
}

interface ALRPayload {
  frameSeq: number;
  currentData: Uint8Array;
  prev1Data: Uint8Array | null;
  prev2Data: Uint8Array | null;
}

// ALR format: [frameSeq (4B)][currentLen (4B)][currentData][prev1Len (4B)][prev1Data][prev2Len (4B)][prev2Data]
// Non-ALR format: [frameSeq (4B)][raw protobuf data]
function parseALRPayload(
  decompressed: Uint8Array,
  hasRedundant: boolean,
): ALRPayload | null {
  if (decompressed.byteLength < 4) return null;

  const view = new DataView(
    decompressed.buffer,
    decompressed.byteOffset,
    decompressed.byteLength,
  );
  let offset = 0;

  const frameSeq = view.getUint32(offset, true);
  offset += 4;

  if (!hasRedundant) {
    const currentData = decompressed.slice(offset);
    return { frameSeq, currentData, prev1Data: null, prev2Data: null };
  }

  if (decompressed.byteLength < offset + 4) return null;
  const currentLen = view.getUint32(offset, true);
  offset += 4;
  if (decompressed.byteLength < offset + currentLen) return null;
  const currentData = decompressed.slice(offset, offset + currentLen);
  offset += currentLen;

  let prev1Data: Uint8Array | null = null;
  if (decompressed.byteLength >= offset + 4) {
    const prev1Len = view.getUint32(offset, true);
    offset += 4;
    if (prev1Len > 0 && decompressed.byteLength >= offset + prev1Len) {
      prev1Data = decompressed.slice(offset, offset + prev1Len);
      offset += prev1Len;
    }
  }

  let prev2Data: Uint8Array | null = null;
  if (decompressed.byteLength >= offset + 4) {
    const prev2Len = view.getUint32(offset, true);
    offset += 4;
    if (prev2Len > 0 && decompressed.byteLength >= offset + prev2Len) {
      prev2Data = decompressed.slice(offset, offset + prev2Len);
    }
  }

  return { frameSeq, currentData, prev1Data, prev2Data };
}

/**
 * Animation frame processor.
 *
 * Stateful per-track parser: consumes encoded video frames and emits
 * animation protocol events. Frames that do not match the animation protocol
 * are reported via the return value so the caller can pass them through.
 *
 * @internal
 */
export class AnimationFrameProcessor {
  /** @internal */
  private readonly onEvent: (evt: AnimationTransformEvent) => void;

  // Parser state
  /** @internal */
  private receiverMetaCount = 0;
  /** @internal */
  private lastLogTime = 0;
  /** @internal */
  private totalFrames = 0;
  /** @internal */
  private framesWithMeta = 0;
  /** @internal */
  private wasIdle = true;
  /** @internal */
  private isInStartTransition = false;
  /** @internal */
  private isInEndTransition = false;

  // ALR (Application-Level Redundancy) state for loss detection
  /** @internal */
  private lastReceivedTimestamp: number | null = null;
  /** @internal */
  private framesRecovered = 0;
  /** @internal */
  private framesLost = 0;
  /** Expected timestamp increment per animation frame (90000 Hz * 40ms = 3600) */
  /** @internal */
  private static readonly EXPECTED_TIMESTAMP_INCREMENT = 3600;

  // Sequence tracking
  /** @internal */
  private lastRenderedSeq = -1;
  /** @internal */
  private framesOutOfOrder = 0;
  /** @internal */
  private framesDuplicate = 0;
  /** @internal */
  private framesDropped = 0;
  /**
   * Packets dropped because their payload would not decompress or parse.
   * @internal
   */
  private framesUndecodable = 0;
  /** @internal */
  private framesSent = 0;

  constructor(onEvent: (evt: AnimationTransformEvent) => void) {
    this.onEvent = onEvent;
  }

  /**
   * Process one encoded frame.
   *
   * @param data - Raw frame data (after depacketization)
   * @param timestamp - RTP timestamp of the frame
   * @param strict - When true, frames must exactly match the animation
   *   protocol layout (used when sharing a pipeline with real video tracks).
   *   When false, every frame is treated as animation data.
   * @returns true if the frame was consumed as animation data, false if it
   *   should be passed through
   */
  processFrame(
    data: Uint8Array,
    timestamp: number,
    strict: boolean,
  ): boolean {
    this.totalFrames++;

    if (strict && !sniffAnimationFrame(data)) {
      return false;
    }

    if (data.length <= VP8_FRAME_HEADER_SIZE) {
      return strict ? false : true;
    }

    const animData = data.subarray(VP8_FRAME_HEADER_SIZE);
    const parsed = parseMetadataHeader(animData);
    if (!parsed) {
      return strict ? false : true;
    }

    this.handleMetadata(parsed, timestamp);
    return true;
  }

  /** @internal */
  private handleMetadata(meta: FrameMetadata, timestamp: number): void {
    this.framesWithMeta++;
    this.receiverMetaCount++;

    const isIdle = isIdlePacket(meta.flags);
    const isStart = isStartPacket(meta.flags);

    // ALR: Detect frame loss and recover using redundant data
    if (this.lastReceivedTimestamp !== null && !isIdle && !isStart) {
      const timestampDelta = timestamp - this.lastReceivedTimestamp;
      if (
        timestampDelta >
        AnimationFrameProcessor.EXPECTED_TIMESTAMP_INCREMENT * 1.5
      ) {
        const missedFrames =
          Math.round(
            timestampDelta /
              AnimationFrameProcessor.EXPECTED_TIMESTAMP_INCREMENT,
          ) - 1;
        this.framesLost += missedFrames;

        if (meta.hasRedundant && isGzipped(meta.flags)) {
          decompressGzip(meta.protobufData)
            .then((decompressed) => {
              const parsed = parseALRPayload(decompressed, true);
              if (parsed) {
                const currentSeq = parsed.frameSeq;

                const framesToRecover: Array<{
                  data: Uint8Array;
                  seq: number;
                }> = [];

                if (missedFrames >= 2 && parsed.prev2Data) {
                  framesToRecover.push({
                    data: parsed.prev2Data,
                    seq: currentSeq - 2,
                  });
                }
                if (missedFrames >= 1 && parsed.prev1Data) {
                  framesToRecover.push({
                    data: parsed.prev1Data,
                    seq: currentSeq - 1,
                  });
                }

                const recovered = framesToRecover.length;
                if (recovered > 0) {
                  this.framesRecovered += recovered;

                  for (const frame of framesToRecover) {
                    this.sendAnimationEvent(
                      frame.data,
                      meta.flags & ~PACKET_FLAG_REDUNDANT,
                      frame.seq,
                      true,
                    );
                  }
                }

                this.sendAnimationEvent(
                  parsed.currentData,
                  meta.flags & ~PACKET_FLAG_REDUNDANT,
                  currentSeq,
                  false,
                );
              }
            })
            .catch((err) => {
              this.onEvent({
                type: 'error',
                error: `ALR decompression error: ${err}`,
              });
            });

          this.lastReceivedTimestamp = timestamp;
          this.reportStats(meta.protobufLength);
          return;
        }
      }
    }

    if (!isIdle) {
      this.lastReceivedTimestamp = timestamp;
    }

    if (isStart) {
      this.lastReceivedTimestamp = timestamp;
      this.framesRecovered = 0;
      this.framesLost = 0;
      this.lastRenderedSeq = -1;
      this.framesOutOfOrder = 0;
      this.framesDuplicate = 0;
      this.framesDropped = 0;
      this.framesSent = 0;
    }

    const isTransition = isTransitionPacket(meta.flags);
    const isTransitionEnd = isTransitionEndPacket(meta.flags);

    if (isIdle) {
      // An idle packet older than the newest packet already seen was sent
      // before that one and merely arrived after it. The server's send order
      // is fixed — idle, round, idle — so a genuine return to idle can never
      // carry an earlier timestamp than the round it ends. Acting on it would
      // close a round that is still being delivered, resetting tracking and
      // restarting the jitter buffer mid-speech; on WebKit, where packets are
      // handed over out of order, that happened several times per round.
      //
      // Only idle is filtered this way: it is the one packet type whose
      // meaning is "everything before this is over", so a stale one is
      // actively harmful rather than merely redundant.
      if (
        this.lastReceivedTimestamp !== null &&
        timestamp < this.lastReceivedTimestamp
      ) {
        this.reportStats(meta.protobufLength);
        return;
      }

      if (!this.wasIdle) {
        this.onEvent({ type: 'idleStart' });
        this.wasIdle = true;
      }
      this.isInStartTransition = false;
      this.isInEndTransition = false;
    } else if (isTransition && meta.protobufLength > 0) {
      this.wasIdle = false;

      if (!this.isInStartTransition) {
        this.isInStartTransition = true;
        this.isInEndTransition = false;
        this.emitMaybeCompressed(meta, 'transition');
      }
    } else if (isTransitionEnd && meta.protobufLength > 0) {
      if (!this.isInEndTransition) {
        this.isInEndTransition = true;
        // The round is over here, so the next start transition belongs to the
        // next one and must be let through.
        this.isInStartTransition = false;
        this.emitMaybeCompressed(meta, 'transitionEnd');
      }
    } else if (meta.protobufLength > 0) {
      // Normal animation packet
      if (this.wasIdle) {
        this.wasIdle = false;
      }
      // The transition latches are deliberately left alone here.
      //
      // Clearing them treated "a stream frame arrived" as "the transition is
      // over", which holds only while packets arrive in the order they were
      // sent. Reordered, a round comes through as T . T . T — and every
      // transition packet after the first found the latch cleared and was
      // emitted as another round start, resetting tracking and restarting the
      // jitter buffer at a single frame each time. What ends a round is the
      // transition-end packet, or idle — both of which reset these.

      if (isGzipped(meta.flags)) {
        decompressGzip(meta.protobufData)
          .then((decompressed) => {
            const parsed = parseALRPayload(decompressed, meta.hasRedundant);
            if (parsed) {
              this.sendAnimationEvent(
                parsed.currentData,
                meta.flags & ~PACKET_FLAG_REDUNDANT,
                parsed.frameSeq,
                false,
              );
            } else {
              this.framesUndecodable++;
            }
          })
          .catch(() => {
            // Counted rather than swallowed: a packet failing here used to
            // vanish without touching any counter, leaving a round that lost
            // most of its frames this way indistinguishable from one the
            // server never sent.
            this.framesUndecodable++;
          });
      } else {
        this.emitRawProtobuf(meta);
      }
    }

    this.reportStats(meta.protobufLength);
  }

  /** @internal */
  private emitMaybeCompressed(
    meta: FrameMetadata,
    kind: 'transition' | 'transitionEnd',
  ): void {
    if (isGzipped(meta.flags)) {
      decompressGzip(meta.protobufData)
        .then((decompressed) => {
          const buffer = copyToArrayBuffer(decompressed);
          if (kind === 'transition') {
            this.onEvent({ type: 'transition', protobufData: buffer, flags: meta.flags });
          } else {
            this.onEvent({
              type: 'transitionEnd',
              protobufData: buffer,
              flags: meta.flags,
            });
          }
        })
        .catch((err) => {
          this.onEvent({
            type: 'error',
            error: `Gzip decompress error (${kind}): ${err}`,
          });
        });
    } else {
      const buffer = copyToArrayBuffer(meta.protobufData);
      if (kind === 'transition') {
        this.onEvent({ type: 'transition', protobufData: buffer, flags: meta.flags });
      } else {
        this.onEvent({
          type: 'transitionEnd',
          protobufData: buffer,
          flags: meta.flags,
        });
      }
    }
  }

  /** @internal */
  private emitRawProtobuf(meta: FrameMetadata): void {
    const isStart = isStartPacket(meta.flags);
    const isEnd = isEndPacket(meta.flags);
    const buffer = copyToArrayBuffer(meta.protobufData);

    this.onEvent({
      type: 'animation',
      protobufData: buffer,
      flags: meta.flags,
      isIdle: false,
      isStart,
      isEnd,
      frameSeq: -1, // Unknown sequence
    });
  }

  /**
   * Send animation data with sequence tracking.
   * @internal
   */
  private sendAnimationEvent(
    protobufData: Uint8Array,
    flags: number,
    frameSeq: number,
    isRecovered: boolean,
  ): boolean {
    const isIdle = isIdlePacket(flags);
    const isStart = isStartPacket(flags);
    const isEnd = isEndPacket(flags);

    if (frameSeq <= this.lastRenderedSeq && this.lastRenderedSeq !== -1 && !isStart) {
      if (frameSeq === this.lastRenderedSeq) {
        this.framesDuplicate++;
      } else {
        this.framesOutOfOrder++;
      }
      return false;
    }

    if (
      this.lastRenderedSeq !== -1 &&
      frameSeq > this.lastRenderedSeq + 1 &&
      !isStart
    ) {
      const gap = frameSeq - this.lastRenderedSeq - 1;
      this.framesDropped += gap;
    }

    this.framesSent++;
    this.lastRenderedSeq = frameSeq;

    this.onEvent({
      type: 'animation',
      protobufData: copyToArrayBuffer(protobufData),
      flags,
      isIdle,
      isStart,
      isEnd,
      frameSeq,
      isRecovered,
    });

    return true;
  }

  /** @internal */
  private reportStats(protobufLength: number): void {
    const now = performance.now();
    if (now - this.lastLogTime > 1000) {
      this.lastLogTime = now;
      this.onEvent({
        type: 'metadata',
        protobufLength,
        framesPerSec: this.receiverMetaCount,
        totalFrames: this.totalFrames,
        framesWithMeta: this.framesWithMeta,
        framesLost: this.framesLost,
        framesRecovered: this.framesRecovered,
        framesOutOfOrder: this.framesOutOfOrder,
        framesDuplicate: this.framesDuplicate,
        // Undecodable packets are folded in here rather than reported apart:
        // both describe a frame that never reached playback, and the existing
        // stats consumers already treat this field as that total.
        framesDropped: this.framesDropped + this.framesUndecodable,
        framesSent: this.framesSent,
        lastRenderedSeq: this.lastRenderedSeq,
      });
      this.receiverMetaCount = 0;
    }
  }
}

function copyToArrayBuffer(data: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(data.byteLength);
  new Uint8Array(buffer).set(data);
  return buffer;
}
